import {
  FETCH_CONVERSATIONS,
  FETCH_CONVERSATION,
  CREATE_CONVERSATION,
  UPDATE_CONVERSATION,
  DELETE_CONVERSATION,
  FETCH_MESSAGES,
  SEND_MESSAGE,
  UPDATE_MESSAGE,
  DELETE_MESSAGE,
  ADD_PARTICIPANT,
  REMOVE_PARTICIPANT,
  FETCH_UNREAD_COUNT,
  MARK_AS_READ,
  UPLOAD_MESSAGE_ATTACHMENT,
  DELETE_MESSAGE_ATTACHMENT,
  MESSAGING_ERROR,
  MESSAGING_LOADING,
  WEBSOCKET_CONNECTION_STATUS,
  TYPING_INDICATOR,
  USER_STATUS_CHANGE,
  UPDATE_UNREAD_COUNT,
  READ_RECEIPT,
  RECEIVE_MESSAGE,
} from "../actions/constants";

const initialState = {
  conversations: [],
  currentConversation: null,
  messages: {},
  unreadCounts: {},
  totalUnreadCount: 0,
  loading: false,
  error: null,
  socketConnected: false,
  typingUsers: {},
  userStatuses: {},
  readReceipts: {},
};

const messagingReducer = (state = initialState, action) => {
  switch (action.type) {
    case MESSAGING_LOADING:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case MESSAGING_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case FETCH_CONVERSATIONS:
      return {
        ...state,
        // Handle the nested structure of the API response
        conversations: action.payload.data?.conversations || [],
        totalUnreadCount: action.payload.data?.totalUnreadCount || 0,
        loading: false,
        error: null,
      };

    case FETCH_CONVERSATION:
      return {
        ...state,
        currentConversation: action.payload,
        loading: false,
        error: null,
      };

    case CREATE_CONVERSATION:
      return {
        ...state,
        conversations: [action.payload, ...state.conversations],
        currentConversation: action.payload,
        loading: false,
        error: null,
      };

    case UPDATE_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.map((conversation) =>
          conversation._id === action.payload._id
            ? action.payload
            : conversation
        ),
        currentConversation:
          state.currentConversation?._id === action.payload._id
            ? action.payload
            : state.currentConversation,
        loading: false,
        error: null,
      };

    case DELETE_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations?.filter(
          (conversation) => conversation._id !== action.payload
        ),
        currentConversation:
          state.currentConversation?._id === action.payload
            ? null
            : state.currentConversation,
        messages: {
          ...state.messages,
          [action.payload]: undefined,
        },
        loading: false,
        error: null,
      };

    case FETCH_MESSAGES:
      const { conversationId, messages, hasMore, page } = action.payload;
      
      // Create a sorted array of messages to ensure consistent ordering
      let sortedMessages = [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      
      // Combine with existing messages if not the first page
      let combinedMessages;
      if (page === 1) {
        combinedMessages = sortedMessages;
      } else {
        // For pagination, combine and remove duplicates
        const existingMessages = state.messages[conversationId]?.data || [];
        const messageMap = new Map();
        
        // Add existing messages
        existingMessages.forEach(msg => messageMap.set(msg._id, msg));
        // Add new messages (will overwrite if duplicate)
        sortedMessages.forEach(msg => messageMap.set(msg._id, msg));
        
        combinedMessages = Array.from(messageMap.values());
      }
          
      // Ensure final sort to maintain chronological order
      combinedMessages = combinedMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      
      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: {
            data: combinedMessages,
            hasMore,
            currentPage: page,
          },
        },
        loading: false,
        error: null,
      };

    case SEND_MESSAGE: {
      const { conversationId: convId, message, isRealTime } = action.payload;
      const existing = state.messages[convId]?.data || [];

      let updatedData;
      const hasTempId = message.tempId && message._id !== message.tempId;

      if (hasTempId) {
        // Real message arrived — replace the optimistic temp entry
        const tempIdx = existing.findIndex(
          (m) => m._id === message.tempId || m.tempId === message.tempId
        );
        if (tempIdx !== -1) {
          updatedData = [...existing];
          updatedData[tempIdx] = message;
        } else {
          // Temp not found; avoid appending if real _id already present
          updatedData = existing.some((m) => m._id === message._id)
            ? existing
            : [...existing, message];
        }
      } else {
        // Optimistic temp message — just append
        updatedData = [...existing, message];
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [convId]: {
            ...state.messages[convId],
            data: updatedData.sort(
              (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
            ),
          },
        },
        conversations: state.conversations.map((conversation) => {
          if (conversation._id === convId) {
            return {
              ...conversation,
              lastMessageAt: message.createdAt,
              lastMessageText: message.text,
              lastMessageSender: message.sender,
            };
          }
          return conversation;
        }),
        loading: !isRealTime,
        error: null,
      };
    }

    case UPDATE_MESSAGE:
      const { conversationId: updateConvId, tempId, messageId, message: updatedMessage } = action.payload;
      return {
        ...state,
        messages: {
          ...state.messages,
          [updateConvId]: {
            ...state.messages[updateConvId],
            data: state.messages[updateConvId]?.data.map(msg => 
              (tempId && msg._id === tempId) || (messageId && msg._id === messageId) 
                ? { ...updatedMessage, status: 'sent' } 
                : msg
            ) || [],
          },
        },
        loading: false,
        error: null,
      };

    case DELETE_MESSAGE:
      const { conversationId: cId, messageId: delMessageId } = action.payload;
      return {
        ...state,
        messages: {
          ...state.messages,
          [cId]: {
            ...state.messages[cId],
            data:
              state.messages[cId]?.data.filter(
                (msg) => msg._id !== delMessageId
              ) || [],
          },
        },
        loading: false,
        error: null,
      };

    case ADD_PARTICIPANT:
      const { conversationId: convIdAdd, participant } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) => {
          if (conversation._id === convIdAdd) {
            return {
              ...conversation,
              participants: [...conversation.participants, participant],
            };
          }
          return conversation;
        }),
        currentConversation:
          state.currentConversation?._id === convIdAdd
            ? {
                ...state.currentConversation,
                participants: [
                  ...state.currentConversation.participants,
                  participant,
                ],
              }
            : state.currentConversation,
        loading: false,
        error: null,
      };

    case REMOVE_PARTICIPANT:
      const { conversationId: convIdRemove, userId } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((conversation) => {
          if (conversation._id === convIdRemove) {
            return {
              ...conversation,
              participants: conversation.participants.filter(
                (p) => p.user._id !== userId
              ),
            };
          }
          return conversation;
        }),
        currentConversation:
          state.currentConversation?._id === convIdRemove
            ? {
                ...state.currentConversation,
                participants:
                  state.currentConversation.participants.filter(
                    (p) => p.user._id !== userId
                  ),
              }
            : state.currentConversation,
        loading: false,
        error: null,
      };

    case FETCH_UNREAD_COUNT: {
      // API returns { success, data: { totalUnreadCount, conversationCounts: [{conversationId, unreadCount}] } }
      const conversationCounts = action.payload.data?.conversationCounts || [];
      const counts = conversationCounts.reduce((acc, { conversationId, unreadCount }) => {
        acc[conversationId] = unreadCount;
        return acc;
      }, {});
      return {
        ...state,
        unreadCounts: counts,
        totalUnreadCount: action.payload.data?.totalUnreadCount || 0,
        loading: false,
        error: null,
      };
    }

    case MARK_AS_READ:
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [action.payload]: 0,
        },
        totalUnreadCount: Math.max(
          0,
          state.totalUnreadCount - (state.unreadCounts[action.payload] || 0)
        ),
        loading: false,
        error: null,
      };

    case UPLOAD_MESSAGE_ATTACHMENT:
      return {
        ...state,
        loading: false,
        error: null,
      };

    case DELETE_MESSAGE_ATTACHMENT:
      return {
        ...state,
        loading: false,
        error: null,
      };

    // Socket.io related actions
    case WEBSOCKET_CONNECTION_STATUS:
      return {
        ...state,
        socketConnected: action.payload.isConnected,
      };

    case TYPING_INDICATOR:
      const { conversationId: typingConvId, userId: typingUserId, isTyping } = action.payload;
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [typingConvId]: {
            ...state.typingUsers[typingConvId],
            [typingUserId]: {
              isTyping,
              timestamp: Date.now(),
            },
          },
        },
      };

    case USER_STATUS_CHANGE:
      return {
        ...state,
        userStatuses: {
          ...state.userStatuses,
          [action.payload.userId]: {
            status: action.payload.status,
            lastSeen: action.payload.lastSeen,
          },
        },
      };

    case RECEIVE_MESSAGE:
      const { conversationId: receiveConvId, message: receivedMessage } = action.payload;
      
      // Only process if it's not a readStatus type message
      if (receivedMessage.type === "readStatus") {
        return state;
      }
      
      // Check if this message already exists in the conversation
      const messageExists = state.messages[receiveConvId]?.data?.some(
        msg => msg._id === receivedMessage._id
      );
      
      if (messageExists) {
        return state;
      }
      
      // Update conversations list to show the latest message
      const updatedConversations = state.conversations.map(conversation => {
        if (conversation._id === receiveConvId) {
          return {
            ...conversation,
            lastMessageAt: receivedMessage.createdAt,
            lastMessageText: receivedMessage.content,
            lastMessageSender: receivedMessage.sender,
          };
        }
        return conversation;
      });
      
      // Sort conversations by latest message
      const sortedConversations = [...updatedConversations].sort(
        (a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
      );
      
      return {
        ...state,
        messages: {
          ...state.messages,
          [receiveConvId]: {
            ...state.messages[receiveConvId],
            data: [
              ...(state.messages[receiveConvId]?.data || []),
              receivedMessage
            ].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
          },
        },
        conversations: sortedConversations,
      };

    case UPDATE_UNREAD_COUNT:
      const { conversationId: unreadConvId, increment } = action.payload;
      const currentCount = state.unreadCounts[unreadConvId] || 0;
      const newCount = increment ? currentCount + 1 : 0;
      
      return {
        ...state,
        unreadCounts: {
          ...state.unreadCounts,
          [unreadConvId]: newCount,
        },
        totalUnreadCount: increment
          ? state.totalUnreadCount + 1
          : Math.max(0, state.totalUnreadCount - currentCount),
      };

    case READ_RECEIPT:
      const { conversationId: readConvId, userId: readUserId, lastReadMessageId, lastReadAt } = action.payload;
      
      // Update read receipts for the conversation
      return {
        ...state,
        readReceipts: {
          ...state.readReceipts,
          [readConvId]: {
            ...state.readReceipts[readConvId],
            [readUserId]: {
              lastReadMessageId,
              lastReadAt
            }
          }
        },
        // Update message status to 'read' if the message was read by another user
        messages: {
          ...state.messages,
          [readConvId]: {
            ...state.messages[readConvId],
            data: state.messages[readConvId]?.data?.map(msg => {
              // If message is before or equal to lastReadMessageId and was sent by current user
              const currentUser = JSON.parse(localStorage.getItem("profile"))?.result;
              
              // Add proper null checks to avoid "Cannot read properties of undefined" error
              if (
                msg?.sender && 
                currentUser && 
                msg.sender._id === currentUser._id && 
                msg._id && 
                lastReadMessageId && 
                msg._id <= lastReadMessageId
              ) {
                return { ...msg, status: 'read' };
              }
              return msg;
            }) || []
          }
        }
      };

    default:
      return state;
  }
};

export default messagingReducer;
